import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, User, Menu, X, Search, Package, LogOut, LayoutDashboard, Heart, Anchor, Mail, Send, Settings } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import logger from '../../lib/logger';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { WishlistBadge } from '../Wishlist';
import { SearchSuggestions } from '../SearchSuggestions';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StoreLayout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const { cart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [footerEmail, setFooterEmail] = useState('');
  const [footerSubmitting, setFooterSubmitting] = useState(false);
  const [footerSubscribed, setFooterSubscribed] = useState(false);

  React.useEffect(() => {
    const q = new URLSearchParams(location.search).get('search');
    setSearchQuery(q || '');
    setSearchFocused(false);
  }, [location.pathname, location.search]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchFocused(false);
      if (document.activeElement) document.activeElement.blur();
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleFooterSubscribe = async (e) => {
    e.preventDefault();
    if (!footerEmail || footerSubmitting) return;

    setFooterSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/aisha/email-list/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: footerEmail,
          source: 'footer',
          tags: ['newsletter', 'footer_signup']
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setFooterSubscribed(true);
        toast.success(`Welcome to our faith community! Use code ${data.discount_code || 'WELCOME10'} for 10% off!`);
        setFooterEmail('');
      }
    } catch (error) {
      logger.error('Footer subscription error:', error);
      toast.error('Something went wrong. Please try again.');
    }
    setFooterSubmitting(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navLinks = [
    { href: '/products', label: 'All Products' },
    { href: '/collections', label: 'Collections' },
    { href: '/photo-gifts', label: 'Print Your Photo' },
    { href: '/voyage', label: 'The Vessel' },
    { href: '/products?category=fashion', label: 'Fashion' },
    { href: '/products?category=Tech %26 Gadgets', label: 'Electronics' },
    { href: '/blog', label: 'Blog' },
    { href: '/track', label: 'Track Order' },
    { href: '/products?category=Home %26 Living', label: 'Home & Living' },
    { href: '/pets', label: 'Pet Deck' },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b-2 border-gold shadow-gold">
        {/* Top bar - Gold gradient */}
        <div className="bg-gold-shimmer text-slate-900 py-2 px-4 text-center text-sm font-medium">
          <span className="drop-shadow-sm">✨ Free shipping on orders over $100 | Faith-inspired fashion & home goods ✨</span>
        </div>
        
        {/* Main header */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo with Gold accent */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <div className="relative">
                <Anchor className="h-8 w-8 text-gold" style={{ color: '#D4AF37' }} />
              </div>
              <div className="flex flex-col">
                <span className="font-cinzel text-xl font-bold text-gold-gradient bg-gradient-to-r from-amber-600 via-yellow-500 to-amber-600 bg-clip-text text-transparent">
                  ALL ABOARD SELAH
                </span>
              </div>
            </Link>

            {/* Search - Desktop with gold focus */}
            <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-md mx-8">
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-500" />
                <Input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
                  className="pl-10 w-full border-amber-200 focus:border-amber-400 focus:ring-amber-400"
                  data-testid="search-input"
                  autoComplete="off"
                />
                <SearchSuggestions
                  query={searchQuery}
                  visible={searchFocused}
                  onPick={() => { setSearchFocused(false); setSearchQuery(''); }}
                />
              </div>
            </form>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {/* Cart with gold badge */}
              <Link to="/cart" data-testid="cart-link">
                <Button variant="ghost" size="icon" className="relative hover:bg-amber-50">
                  <ShoppingCart className="h-5 w-5 text-amber-700" />
                  {cart.item_count > 0 && (
                    <Badge 
                      className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0"
                      style={{ backgroundColor: '#D4AF37' }}
                      data-testid="cart-badge"
                    >
                      {cart.item_count}
                    </Badge>
                  )}
                </Button>
              </Link>

              {/* User menu */}
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="hover:bg-amber-50" data-testid="user-menu-trigger">
                      <User className="h-5 w-5 text-amber-700" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 border-amber-200">
                    <div className="px-2 py-1.5 text-sm font-medium text-amber-800">{user.name}</div>
                    <div className="px-2 py-1 text-xs text-slate-500">{user.email}</div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/orders')} data-testid="orders-link">
                      <Package className="mr-2 h-4 w-4" />
                      My Orders
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/account')} data-testid="account-settings-link">
                      <Settings className="mr-2 h-4 w-4" />
                      Account Settings
                    </DropdownMenuItem>
                    {isAdmin && (
                      <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-link">
                        <LayoutDashboard className="mr-2 h-4 w-4" />
                        Admin Dashboard
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                      <LogOut className="mr-2 h-4 w-4" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <div className="hidden sm:flex items-center gap-2">
                  <Link to="/login">
                    <Button variant="ghost" size="sm" className="text-amber-700 hover:bg-amber-50" data-testid="login-link">Login</Button>
                  </Link>
                  <Link to="/register">
                    <Button size="sm" className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-gold" data-testid="register-link">
                      Register
                    </Button>
                  </Link>
                </div>
              )}

              {/* Mobile menu button */}
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden hover:bg-amber-50"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>

          {/* Navigation - Desktop with gold accents */}
          <nav className="hidden md:flex items-center gap-6 py-3 border-t border-amber-100">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium transition-colors hover:text-amber-600 ${
                  location.pathname + location.search === link.href
                    ? 'text-amber-600 font-semibold'
                    : 'text-slate-600'
                }`}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white">
            <div className="px-4 py-3">
              <form onSubmit={handleSearch} className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setSearchFocused(true)}
                    onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
                    className="pl-10 w-full"
                    data-testid="search-input-mobile"
                    autoComplete="off"
                  />
                  <SearchSuggestions
                    query={searchQuery}
                    visible={searchFocused}
                    onPick={() => { setSearchFocused(false); setSearchQuery(''); setMobileMenuOpen(false); }}
                  />
                </div>
              </form>
              <nav className="space-y-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="block py-2 text-sm font-medium text-slate-600 hover:text-blue-600"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
                {!user && (
                  <>
                    <Link
                      to="/login"
                      className="block py-2 text-sm font-medium text-slate-600 hover:text-blue-600"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Login
                    </Link>
                    <Link
                      to="/register"
                      className="block py-2 text-sm font-medium text-slate-600 hover:text-blue-600"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Register
                    </Link>
                  </>
                )}
              </nav>
            </div>
          </div>
        )}
      </header>

      {/* Main content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white">
        {/* Newsletter Signup Section */}
        <div className="bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-left">
                <h3 className="text-xl font-bold text-white flex items-center gap-2 justify-center md:justify-start">
                  <Mail className="w-6 h-6" />
                  Join Our Faith Community
                </h3>
                <p className="text-amber-100 text-sm mt-1">
                  Get 10% off + exclusive faith-inspired content & early access to new collections
                </p>
              </div>
              {footerSubscribed ? (
                <div className="bg-white/20 rounded-lg px-6 py-3 text-white font-medium">
                  ✓ You&apos;re in! Check your inbox for your discount code.
                </div>
              ) : (
                <form onSubmit={handleFooterSubscribe} className="flex gap-2 w-full md:w-auto">
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={footerEmail}
                    onChange={(e) => setFooterEmail(e.target.value)}
                    className="bg-white/90 border-0 text-slate-900 placeholder:text-slate-500 min-w-0 flex-1 sm:min-w-[250px]"
                    data-testid="footer-email-input"
                    required
                  />
                  <Button 
                    type="submit"
                    disabled={footerSubmitting}
                    className="bg-slate-900 hover:bg-slate-800 text-white px-4 sm:px-6 shrink-0"
                    data-testid="footer-subscribe-btn"
                  >
                    {footerSubmitting ? (
                      <span className="animate-pulse">...</span>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Subscribe
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Package className="h-6 w-6 text-indigo-400" />
                <span className="font-heading text-lg font-bold">ALL ABOARD SELAH</span>
              </div>
              <p className="text-slate-400 text-sm mb-4">
                Premium fashion & lifestyle products delivered directly to your door.
              </p>
              <div className="flex gap-3">
                <a href="https://instagram.com/allaboardselah" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-pink-400 transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
                <a href="https://facebook.com/allaboardselah" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-blue-400 transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a href="https://tiktok.com/@allaboardselah" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
                </a>
                <a href="https://snapchat.com/add/steve_tucke8031" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-yellow-400 transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z"/></svg>
                </a>
                <a href="https://reddit.com/user/Vivid-Gas-2073" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-orange-500 transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/></svg>
                </a>
                <a href="https://pinterest.com/sdtuck92" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-red-500 transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/></svg>
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-heading font-bold mb-4">Shop</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/products" className="hover:text-white">All Products</Link></li>
                <li><Link to="/products?category=fashion" className="hover:text-white">Fashion & Apparel</Link></li>
                <li><Link to="/products?category=Jewelry" className="hover:text-white">Jewelry</Link></li>
                <li><Link to="/products?category=Accessories" className="hover:text-white">Accessories</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-bold mb-4">Categories</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/products?category=Winter Collection" className="hover:text-white">Winter Collection</Link></li>
                <li><Link to="/products?category=Summer Praise" className="hover:text-white">Summer Praise</Link></li>
                <li><Link to="/products?category=Home %26 Living" className="hover:text-white">Home & Living</Link></li>
                <li><Link to="/products?category=Tech %26 Gadgets" className="hover:text-white">Electronics</Link></li>
                <li><Link to="/pets" className="hover:text-white" data-testid="footer-pet-deck-link">Blessed Paws · Pet Supplies</Link></li>
                <li><Link to="/products?category=Garden & Outdoor" className="hover:text-white">Garden & Outdoor</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-bold mb-4">Account</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/orders" className="hover:text-white">My Orders</Link></li>
                <li><Link to="/cart" className="hover:text-white">Shopping Cart</Link></li>
                <li><Link to="/affiliate" className="hover:text-white text-amber-400 font-semibold">Become an Affiliate</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-bold mb-4">Support</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="mailto:allaboardselah@gmail.com" className="hover:text-white">Contact Us</a></li>
                <li><Link to="/shipping" className="hover:text-white">Shipping Info</Link></li>
                <li><Link to="/returns" className="hover:text-white">Returns</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm text-slate-400">
            <p>&copy; {new Date().getFullYear()} ALL ABOARD SELAH. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default StoreLayout;
